import { useEffect, useState, type FormEvent } from "react";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Eye, EyeOff, LockKeyhole, UserRound } from "lucide-react";
import { ApiError, IS_DEMO } from "./api";
import { Modal } from "./components";

export interface Member {
  id: string;
  identifier: string;
  nickname: string;
  bio: string;
  memberType: string;
  diamonds: number;
}
interface MemberConfig {
  settings: {
    registerEnabled: boolean;
    phoneEnabled: boolean;
    emailEnabled: boolean;
    verificationMode: string;
    testCode?: string;
  };
}
let nativeToken = "";
async function memberRequest(
  path: string,
  method = "GET",
  body?: unknown,
): Promise<any> {
  const native = Capacitor.isNativePlatform();
  const base = native
    ? import.meta.env.VITE_NATIVE_API_BASE_URL ||
      "https://ytapp.bbccys.com/api/v1"
    : import.meta.env.VITE_API_BASE_URL || "/api/v1";
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(body ? { "Content-Type": "application/json" } : {}),
    ...(nativeToken && native
      ? { Authorization: `Bearer ${nativeToken}` }
      : {}),
  };
  try {
    let status: number, data: any;
    if (native) {
      const response = await CapacitorHttp.request({
        url: base.replace(/\/$/, "") + "/members" + path,
        method,
        headers,
        ...(body ? { data: body } : {}),
        connectTimeout: 12000,
        readTimeout: 12000,
      });
      status = response.status;
      data =
        typeof response.data === "string"
          ? JSON.parse(response.data)
          : response.data;
    } else {
      const response = await fetch(
        base.replace(/\/$/, "") + "/members" + path,
        {
          method,
          headers,
          credentials: "include",
          ...(body ? { body: JSON.stringify(body) } : {}),
          signal: AbortSignal.timeout(12000),
        },
      );
      status = response.status;
      data = await response.json();
    }
    if (status < 200 || status >= 300)
      throw new ApiError(data?.error || "账号服务暂不可用", status);
    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError("无法连接账号服务，请稍后重试");
  }
}
export function useMember() {
  const [user, setUser] = useState<Member | null>(null);
  const [loading, setLoading] = useState(!IS_DEMO);
  const [error, setError] = useState("");
  useEffect(() => {
    if (IS_DEMO) return;
    let active = true;
    memberRequest("/me")
      .then((data) => {
        if (active) setUser(data.user);
      })
      .catch((error) => {
        if (active && (!(error instanceof ApiError) || error.status !== 401))
          setError(error.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);
  return {
    user,
    loading,
    error,
    async signIn(mode: "login" | "register", body: Record<string, string>) {
      const data = await memberRequest("/" + mode, "POST", body);
      if (!data.user?.id) throw new ApiError("账号数据格式异常");
      // Browser uses only HttpOnly cookies. Native bearer stays in memory, never localStorage.
      if (Capacitor.isNativePlatform()) nativeToken = data.token || "";
      setUser(data.user);
      setError("");
      return data.user as Member;
    },
    async updateProfile(nickname: string, bio: string) {
      const data = await memberRequest("/me", "PUT", { nickname, bio });
      setUser(data.user);
      return data.user as Member;
    },
    async signOut() {
      await memberRequest("/logout", "POST", {});
      nativeToken = "";
      setUser(null);
      setError("");
    },
  };
}
export function AuthDialog({
  onClose,
  onSuccess,
  signIn,
}: {
  onClose: () => void;
  onSuccess: (user: Member) => void;
  signIn: ReturnType<typeof useMember>["signIn"];
}) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [config, setConfig] = useState<MemberConfig | null>(null);
  const [configError, setConfigError] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [code, setCode] = useState("");
  const [accepted, setAccepted] = useState(false);
  const loadConfig = () =>
    memberRequest("/public-config")
      .then(setConfig)
      .catch((error) => setConfigError(error.message));
  useEffect(() => {
    void loadConfig();
  }, []);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (mode === "register" && !accepted) {
      setError("请确认账号信息将保存在平台服务器");
      return;
    }
    setBusy(true);
    try {
      onSuccess(
        await signIn(mode, {
          identifier,
          password,
          ...(mode === "register"
            ? {
                nickname,
                code,
                source: Capacitor.isNativePlatform()
                  ? "APP注册页"
                  : "新H5注册页",
              }
            : {}),
        }),
      );
    } catch (error) {
      setError(error instanceof Error ? error.message : "登录失败");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={mode === "login" ? "登录樱桃视频" : "创建账号"}
      onClose={busy ? () => {} : onClose}
    >
      <div className="auth-brand">
        <span>CHERRY</span>
        <h2>每一帧，都值得遇见</h2>
        <p>登录后管理你的账号与个人资料</p>
      </div>
      <div className="auth-tabs">
        <button
          type="button"
          disabled={busy}
          className={mode === "login" ? "selected" : ""}
          onClick={() => {
            setMode("login");
            setError("");
          }}
        >
          登录
        </button>
        <button
          type="button"
          disabled={busy || !config?.settings.registerEnabled}
          className={mode === "register" ? "selected" : ""}
          onClick={() => {
            setMode("register");
            setError("");
          }}
        >
          注册
        </button>
      </div>
      {configError && (
        <p className="auth-error">
          {configError}{" "}
          <button
            onClick={() => {
              setConfigError("");
              void loadConfig();
            }}
          >
            重试
          </button>
        </p>
      )}
      <form className="edit-profile auth-form" onSubmit={submit}>
        <label>
          手机号 / 邮箱
          <div className="auth-input">
            <UserRound size={18} />
            <input
              aria-label="手机号或邮箱"
              autoComplete="username"
              inputMode="email"
              required
              maxLength={254}
              placeholder="输入手机号或邮箱"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
          </div>
        </label>
        <label>
          密码
          <div className="auth-input">
            <LockKeyhole size={18} />
            <input
              aria-label="登录密码"
              type={visible ? "text" : "password"}
              autoComplete={
                mode === "login" ? "current-password" : "new-password"
              }
              required
              minLength={mode === "register" ? 8 : 1}
              maxLength={128}
              placeholder={
                mode === "register" ? "设置至少8位密码" : "输入登录密码"
              }
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              aria-label={visible ? "隐藏密码" : "显示密码"}
              type="button"
              onClick={() => setVisible(!visible)}
            >
              {visible ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>
        {mode === "register" && (
          <>
            <label>
              昵称
              <input
                aria-label="注册昵称"
                maxLength={20}
                placeholder="给自己取个名字"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
              />
            </label>
            {config?.settings.verificationMode !== "无需验证" && (
              <label>
                验证码
                <input
                  aria-label="注册验证码"
                  autoComplete="one-time-code"
                  required
                  inputMode="numeric"
                  maxLength={12}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="输入验证码"
                />
              </label>
            )}
            {config?.settings.verificationMode === "测试验证码" && (
              <p className="auth-note">
                平台当前启用测试验证码：{config.settings.testCode}
                。短信与邮件验证尚未启用。
              </p>
            )}
            <label className="auth-consent">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              我确认注册信息将保存在平台服务器
            </label>
          </>
        )}
        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}
        <button
          className="primary full-width"
          type="submit"
          disabled={
            busy || (mode === "register" && !config?.settings.registerEnabled)
          }
        >
          {busy ? "正在提交…" : mode === "login" ? "登录" : "注册并登录"}
        </button>
        <p className="auth-note">
          {mode === "login"
            ? "支持已有的普通用户账号"
            : "注册方式与开放状态由后台控制"}
        </p>
      </form>
    </Modal>
  );
}
